import moment from 'moment';
import { sha256 } from 'js-sha256';
import { AssignmentType, FormFieldDataType, GenderPatientContext, ValidationActionType } from 'models/enum';
import { ChartFieldContent, ChartMetadata, PatientContext } from 'models/ui';
import { BARCODE_CHART_PROP, BARCODE_LENGTH, Locales, NAV_ID, PATIENT_ID_PROP, USA_BARCODE_PREFIX } from 'constants/app.constant';
import { calculateAge } from 'helpers/datetime.helper';
import { BarcodePatient, MedicationItem } from 'components/features/assignment/barcode-modal/barcode-modal.types';

const NON_VALUE_TOKENS = new Set([
  'na',
  'n/a',
  'none',
  'no known allergies',
  'nka',
  'other'
]);

const asText = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

export interface BarcodeDropdownItem {
  id: string;
  name: string;
  value: string;
}

export type BarcodeNormalizedDetails = {
  patient: BarcodePatient;
  medications: MedicationItem[];
};

export type BarcodeFragment = {
  navElementId?: string;
  active?: boolean;
  chartData?: Record<string, unknown>;
  fragmentId?: string;
  chartingAt?: string;
  createdAt?: string;
};

export const normalizeAllergyTokens = (values: string[]): string[] => Array.from(new Set(
  values
    .map((value) => value.trim())
    .filter((value) => {
      if (!value) return false;
      return !NON_VALUE_TOKENS.has(value.toLowerCase());
    })
));

export const extractAllergyTokensFromChartData = (chartData: Record<string, unknown>): string[] => {
  const sections = Array.isArray(chartData?.records) ? chartData.records as Array<Record<string, unknown>> : [];
  const allergyTokens: string[] = [];

  sections.forEach((section) => {
    const records = Array.isArray(section?.records) ? section.records as Array<Record<string, unknown>> : [];

    let allergenOther = '';
    let allergen = '';
    let medicationOther = '';
    let medication = '';
    let reactionsOther = '';
    records.forEach((record) => {
      const field = asText(record?.formField);
      const content = asText(record?.content);
      if (!field || !content) return;

      if (field === 'allergenOther') allergenOther = content;
      else if (field === 'allergen') allergen = content;
      else if (field === 'allergenMedicationOther') medicationOther = content;
      else if (field === 'medication') medication = content;
      else if (field === 'reactionsOther') reactionsOther = content;
    });

    const label = allergenOther || allergen || medicationOther || medication;
    if (!label) return;

    allergyTokens.push(reactionsOther ? `${label} (${reactionsOther})` : label);
  });

  return normalizeAllergyTokens(allergyTokens);
};

export const buildSessionKey = (assignmentId?: number, simulationEhrId?: string) => `barcode-modal-dob-${assignmentId || simulationEhrId || 'draft'}`;

const normalizeBarcodeField = (value?: string): string => (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const generateBarcodeValue = (medicationName?: string, medicationDose?: string, medicationForm?: string) => {
  const key = `${normalizeBarcodeField(medicationName)}|${normalizeBarcodeField(medicationDose)}|${normalizeBarcodeField(medicationForm)}`;
  return `${USA_BARCODE_PREFIX}${sha256(key).substring(0, BARCODE_LENGTH).toUpperCase()}`;
};

export const collectValuesByKeyPattern = (input: unknown, keyMatcher: (key: string) => boolean): string[] => {
  const values: string[] = [];

  const walk = (node: unknown) => {
    if (!node) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item) => walk(item));
      return;
    }
    if (typeof node !== 'object') {
      return;
    }

    Object.entries(node as Record<string, unknown>).forEach(([key, value]) => {
      if (keyMatcher(key) && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
        values.push(String(value));
      }
      walk(value);
    });
  };

  walk(input);
  return values;
};

export const getFirstChartDataValue = (chartData: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = chartData[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
};

const getFragmentTime = (fragment: any) => {
  const candidate = fragment?.chartingAt || fragment?.createdAt;
  const parsed = moment(candidate);
  return parsed.isValid() ? parsed : null;
};

export const sortByNewestFragmentTime = (firstFragment: any, secondFragment: any) => {
  const firstTime = getFragmentTime(firstFragment);
  const secondTime = getFragmentTime(secondFragment);

  if (!firstTime && !secondTime) return 0;
  if (!firstTime) return 1;
  if (!secondTime) return -1;
  if (firstTime.isSame(secondTime)) return 0;
  return firstTime.isAfter(secondTime) ? -1 : 1;
};

export const normalizeFromFragments = (
  fragments: BarcodeFragment[]
): BarcodeNormalizedDetails => {
  const patientFragments = fragments
    .filter((item) => item?.navElementId === NAV_ID.PATIENT && item?.active)
    .sort(sortByNewestFragmentTime);
  const pharmacyFragments = fragments.filter((item) => item?.navElementId === NAV_ID.PHARMACY);

  const patientDataList = patientFragments.map((fragment) => fragment?.chartData || {});
  const patientData = patientDataList[0] || {};

  const getFirstPatientValue = (matcher: (key: string) => boolean) => {
    for (const chartData of patientDataList) {
      const value = collectValuesByKeyPattern(chartData, matcher)[0];
      if (value) {
        return value;
      }
    }
    return '';
  };

  const patientNameParts = collectValuesByKeyPattern(patientData, (key) => /(first.?name|last.?name|name)/i.test(key));
  const patientName = patientNameParts.slice(0, 2).join(', ') || patientNameParts[0] || '--';
  const patientMrn = getFirstPatientValue((key) => /(mrn|medical.*record)/i.test(key)) || '--';
  const patientId = (patientData[PATIENT_ID_PROP] as string)
    || getFirstPatientValue((key) => /(patient.*id|identifier)/i.test(key))
    || '--';

  const patientInfoAllergies = normalizeAllergyTokens(
    patientDataList
      .map((chartData) => extractAllergyTokensFromChartData(chartData))
      .flat()
  ).join(', ');
  const patientAllergies = patientInfoAllergies || 'NA';
  const patientDobRaw = getFirstPatientValue((key) => /(date.*birth|dob)/i.test(key)) || '';
  const patientAge = calculateAge(patientDobRaw);
  const patientDob = patientDobRaw || '';

  const medications: MedicationItem[] = pharmacyFragments.map((fragment, index) => {
    const chartData = fragment?.chartData || {};
    const isDistractor = chartData.isDistractor === true;
    const medicationName = getFirstChartDataValue(chartData, ['medication', 'name', 'orderItem', 'orderItemOther'])
      || collectValuesByKeyPattern(chartData, (key) => /(order.*item|medication|drug.*name|title|name)/i.test(key))[0]
      || `Medication ${index + 1}`;
    let dose = getFirstChartDataValue(chartData, ['medicationDoseTextbox', 'medicationDose', 'dose', 'strength'])
      || collectValuesByKeyPattern(chartData, (key) => /(dose|strength)/i.test(key))[0]
      || '--';

    if (String(chartData.medicationDose) === 'Special Dose') {
      const specialDose = getFirstChartDataValue(chartData, ['specialDose']);
      const doseUnits = getFirstChartDataValue(chartData, ['doseUnits']);
      const specialDoseLabel = `${specialDose} ${doseUnits}`.trim();
      if (specialDoseLabel) {
        dose = specialDoseLabel;
      }
    }

    const form = getFirstChartDataValue(chartData, ['medicationFormOther', 'medicationForm', 'form'])
      || collectValuesByKeyPattern(chartData, (key) => /form/i.test(key))[0]
      || '';
    const barcodeValue = typeof chartData[BARCODE_CHART_PROP] === 'string'
      ? String(chartData[BARCODE_CHART_PROP])
      : generateBarcodeValue(
        getFirstChartDataValue(chartData, ['medication']),
        getFirstChartDataValue(chartData, ['medicationDose']),
        getFirstChartDataValue(chartData, ['medicationForm'])
      );

    return {
      id: fragment?.fragmentId || `${medicationName}-${index}`,
      medicationName,
      dose,
      form,
      barcodeValue,
      selected: false,
      isDistractor
    };
  });

  return {
    patient: {
      name: patientName,
      mrn: patientMrn,
      age: patientAge,
      patientId,
      allergies: patientAllergies,
      dateOfBirth: patientDob
    },
    medications
  };
};

export const normalizeGenderForPatientContext = (genderValue: string): GenderPatientContext => {
  const normalized = genderValue.trim().toUpperCase();
  if (normalized.startsWith('F')) {
    return GenderPatientContext.FEMALE;
  }
  return GenderPatientContext.MALE;
};

export const parseAgeInDays = (rawDobValue: string): number => {
  if (!rawDobValue) {
    return 1;
  }

  const parsed = moment(rawDobValue, [moment.ISO_8601, 'MM/DD/YYYY', 'MM-DD-YYYY'], true);
  if (!parsed.isValid()) {
    return 1;
  }

  return Math.max(moment().startOf('day').diff(parsed.startOf('day'), 'days'), 1);
};

export const buildPharmacyPatientContext = (
  patientChartData: Record<string, unknown>,
  patientDobKey: string,
  fallbackDobValue: string
): PatientContext => {
  const dobCandidate = typeof patientChartData[patientDobKey] === 'string'
    ? String(patientChartData[patientDobKey])
    : fallbackDobValue;
  const genderCandidate =
    (typeof patientChartData.patientSex === 'string' && patientChartData.patientSex)
    || collectValuesByKeyPattern(patientChartData, (key) => /(patient.*sex|sex|gender)/i.test(key))[0]
    || '';

  return {
    hospitalFloors: [],
    gender: normalizeGenderForPatientContext(genderCandidate),
    ageInDays: parseAgeInDays(dobCandidate),
    assignmentType: AssignmentType.SIMULATION,
    isAuthor: false,
    locale: Locales.EN_US
  };
};

export const normalizeMedicationOptionLabel = (rawValue: string): string => {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return '';
  }

  if (!/^medication/i.test(trimmed)) {
    return trimmed;
  }

  return trimmed
    .replace(/^medication/i, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
};

const MEDICATION_FORM_TOKENS = [
  'Extended Release Tablet',
  'Delayed Release Tablet',
  'Chewable Tablet',
  'Oral Suspension',
  'Oral Solution',
  'Oral Drops',
  'Oral Powder',
  'Tablet',
  'Capsule',
  'Suspension',
  'Solution',
  'Injection',
  'Inhaler',
  'Nebulizer',
  'Suppository',
  'Lozenge',
  'Ointment',
  'Cream',
  'Lotion',
  'Patch',
  'Elixir',
  'Syrup',
  'Drops',
  'Spray',
  'Powder',
  'Film',
  'Gel'
] as const;

export const extractMedicationFormFromOrderLabel = (orderLabel: string, medicationName = ''): string => {
  const normalizedLabel = String(orderLabel || '').replace(/\s+/g, ' ').trim();
  if (!normalizedLabel) {
    return '';
  }

  let segment = normalizedLabel.split(/\s-\s/)[0].trim();
  segment = segment.replace(/\([^)]*\)\s*$/, '').trim();

  if (medicationName) {
    const escapedMedication = medicationName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    segment = segment.replace(new RegExp(`^${escapedMedication}\\s*`, 'i'), '').trim();
  }

  if (!segment) {
    return '';
  }

  const matchedForm = MEDICATION_FORM_TOKENS.find((token) => {
    const tokenPattern = token.replace(/\s+/g, '\\s+');
    return new RegExp(`\\b${tokenPattern}\\b`, 'i').test(segment);
  });

  return matchedForm || '';
};

export const normalizeMedicationIdentity = (value: string): string => normalizeMedicationOptionLabel(value || '').toLowerCase();

export const getMedicationMetadataDisplayValue = (item: ChartFieldContent & { choice?: string }): string => {
  const candidates = [item.label, item.fieldLabel, item.choice, item.value]
    .filter((value): value is string => typeof value === 'string' && !!value.trim())
    .map((value) => value.trim());

  if (!candidates.length) {
    return '';
  }

  const humanReadableCandidate = candidates.find((value) => !/^medication[A-Z0-9]/.test(value));
  return normalizeMedicationOptionLabel(humanReadableCandidate || candidates[0]);
};

export const extractMedicationOptionsFromMetadata = (chartFieldContentSet: ChartFieldContent[]): BarcodeDropdownItem[] => chartFieldContentSet
  .filter((item) => {
    if (String(item?.formFieldId || '').toLowerCase() !== 'medication') return false;
    if (item?.active === false) return false;
    if (![FormFieldDataType.CHOICE, FormFieldDataType.NESTED_CHOICE].includes(item?.dataType)) return false;

    // Match Pharmacy Order behavior: show when isShown=true or null, hide when false.
    const isShown = item?.isShown;
    if (isShown === false) return false;

    return true;
  })
  .sort((left, right) => (left?.order || 0) - (right?.order || 0))
  .map((item) => {
    const metadataItem = item as ChartFieldContent & { choice?: string };
    const displayValue = getMedicationMetadataDisplayValue(metadataItem);
    return {
      id: String(item?.id || displayValue || item?.value || ''),
      name: displayValue,
      value: displayValue
    };
  })
  .filter((item) => !!item.name && item.name.toLowerCase() !== 'other');

const doesMetadataRuleMatchValue = (regex: string | undefined, selectedMedicationValue: string): boolean => {
  if (!regex || !selectedMedicationValue) {
    return false;
  }

  try {
    return new RegExp(regex).test(selectedMedicationValue);
  } catch {
    return false;
  }
};

const findSelectedMedicationContent = (
  chartFieldContentSet: ChartFieldContent[],
  selectedOrderId?: string,
  selectedOrderLabel?: string
): ChartFieldContent | undefined => {
  const byId = chartFieldContentSet.find((item) => item.id === selectedOrderId);
  if (byId) {
    return byId;
  }

  const normalizedSelectedLabel = normalizeMedicationOptionLabel(selectedOrderLabel || '').toLowerCase();
  if (!normalizedSelectedLabel) {
    return undefined;
  }

  return chartFieldContentSet.find((item) => {
    if (item.formFieldId !== 'medication') {
      return false;
    }

    const itemLabel = getMedicationMetadataDisplayValue(item as ChartFieldContent & { choice?: string }).toLowerCase();
    return itemLabel === normalizedSelectedLabel;
  });
};

export const getPharmacyMetadataOptionsForField = (
  chartMetadata: ChartMetadata | null,
  formFieldId: string,
  selectedOrderId?: string,
  selectedOrderLabel?: string,
  strategy: 'visible' | 'prefill' = 'visible'
): string[] => {
  if (!chartMetadata) {
    return [];
  }

  const chartFieldContentSet = chartMetadata.chartFieldContentSet || [];
  const selectedMedication = findSelectedMedicationContent(chartFieldContentSet, selectedOrderId, selectedOrderLabel);
  const selectedMedicationValue = selectedMedication?.value;
  if (!selectedMedicationValue) {
    return [];
  }

  if (strategy === 'prefill') {
    const prefilledOptions = (chartMetadata.chartFieldValidationSet || [])
      .filter((rule) => rule.validationActionType === ValidationActionType.PREFILL)
      .filter((rule) => doesMetadataRuleMatchValue(rule.regex, selectedMedicationValue))
      .flatMap((rule) => rule.targets || [])
      .filter((target) => target.formFieldId === formFieldId && !!target.chartFieldContentId)
      .map((target) => chartFieldContentSet.find((item) => item.id === target.chartFieldContentId))
      .filter((item): item is ChartFieldContent => !!item && item.active !== false && item.isShown !== false)
      .sort((left, right) => (left.order || 0) - (right.order || 0))
      .map((item) => String(item.label || '').trim())
      .filter((label) => !!label);

    return Array.from(new Set(prefilledOptions));
  }

  const validationRuleById = new Map((chartMetadata.chartFieldValidationSet || []).map((rule) => [rule.id, rule]));
  return chartFieldContentSet
    .filter((item) => item.formFieldId === formFieldId)
    .filter((item) => item.active !== false)
    .filter((item) => item.isShown !== false)
    .filter((item) => item.dataType === FormFieldDataType.CHOICE || item.dataType === FormFieldDataType.NESTED_CHOICE)
    .filter((item) => {
      if (!item.validationSetIds?.length) {
        return true;
      }

      return item.validationSetIds.some((ruleId) => {
        const rule = validationRuleById.get(ruleId);
        return doesMetadataRuleMatchValue(rule?.regex, selectedMedicationValue);
      });
    })
    .sort((left, right) => (left.order || 0) - (right.order || 0))
    .map((item) => String(item.label || '').trim())
    .filter((label) => !!label);
};

type SimulationPhaseData = {
  id?: string;
  type?: string;
};

type PhaseChartDataItem = {
  navId?: string;
  chartData?: Record<string, unknown>;
};

const getFirstMedicationValueFromRecords = (chartData: Record<string, unknown>): string => {
  const sections = Array.isArray(chartData?.records) ? chartData.records as Array<Record<string, unknown>> : [];

  for (const section of sections) {
    const records = Array.isArray(section?.records) ? section.records as Array<Record<string, unknown>> : [];
    for (const record of records) {
      const formField = asText(record?.formField).toLowerCase();
      const content = asText(record?.content);
      if (!content) continue;
      if (formField === 'medication' || formField === 'orderitem' || formField === 'orderitemother') {
        return content;
      }
    }
  }

  return '';
};

const getMedicationNameFromChartData = (chartData: Record<string, unknown>): string => {
  const direct = getFirstChartDataValue(chartData, ['medication', 'orderItemOther', 'orderItem', 'name', 'title']);
  const fromRecords = getFirstMedicationValueFromRecords(chartData);
  const fallback = collectValuesByKeyPattern(chartData, (key) => /(order.*item|medication|drug.*name|title|name)/i.test(key))[0] || '';
  return normalizeMedicationOptionLabel(direct || fromRecords || fallback);
};

export const loadDirectContentMedicationOptions = async (
  simulationEhrId: string | undefined,
  getSimulation: (id: string) => Promise<{ data?: { phases?: SimulationPhaseData[] } }>,
  getContentPhaseChartData: (phaseId: string) => Promise<{ data?: PhaseChartDataItem[] }>
): Promise<BarcodeDropdownItem[]> => {
  if (!simulationEhrId) return [];

  try {
    const simulationResponse = await getSimulation(simulationEhrId);
    const allPhases = simulationResponse?.data?.phases || [];
    const phases = allPhases.filter((phase) => phase?.id && phase?.type !== 'OVERVIEW');
    if (!phases.length) return [];

    const medicationMap = new Map<string, BarcodeDropdownItem>();

    const phaseResponses = await Promise.all(
      phases.map(async (phase) => ({
        phase,
        response: await getContentPhaseChartData(phase.id as string)
      }))
    );

    for (const { phase, response } of phaseResponses) {
      const chartDataItems = response?.data ?? [];
      const pharmacyItems = chartDataItems.filter((item) => item?.navId === NAV_ID.PHARMACY);

      pharmacyItems.forEach((item, index) => {
        const medicationName = getMedicationNameFromChartData(item?.chartData || {});
        if (!medicationName || medicationName.toLowerCase() === 'other') {
          return;
        }

        const identity = normalizeMedicationIdentity(medicationName);
        if (!identity || medicationMap.has(identity)) {
          return;
        }

        medicationMap.set(identity, {
          id: `${phase.id}-${index}`,
          name: medicationName,
          value: medicationName
        });
      });
    }

    return Array.from(medicationMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
};

export const loadDirectContentAllergies = async (
  simulationEhrId: string | undefined,
  getSimulation: (id: string) => Promise<{ data?: { phases?: SimulationPhaseData[] } }>,
  getContentPhaseChartData: (phaseId: string) => Promise<{ data?: PhaseChartDataItem[] }>
): Promise<string> => {
  if (!simulationEhrId) return '';

  try {
    const simulationResponse = await getSimulation(simulationEhrId);
    const allPhases = simulationResponse?.data?.phases || [];
    const phases = allPhases.filter((phase) => phase?.id && phase?.type !== 'OVERVIEW');
    if (!phases.length) return '';

    const allergyNamesByPhase = await Promise.all(
      phases.map(async (phase) => {
        try {
          const response = await getContentPhaseChartData(phase.id as string);
          const chartDataItems = response?.data ?? [];
          const allergyItems = chartDataItems.filter(
            (item) => item?.navId === NAV_ID.ALLERGIES || item?.navId === NAV_ID.ALLERGY_INFORMATION
          );
          return normalizeAllergyTokens(
            allergyItems
              .map((item) => extractAllergyTokensFromChartData(item?.chartData || {}))
              .flat()
          );
        } catch {
          return [];
        }
      })
    );

    for (const allergyNames of allergyNamesByPhase) {
      if (allergyNames.length) {
        return allergyNames.join(', ');
      }
    }

    return '';
  } catch {
    return '';
  }
};

/**
 * Limits medication dose input to 12 characters (free text)
 * @param value - The input value
 * @returns Truncated value (max 12 characters)
 */
export const validateMedicationDoseInput = (value: string): string => {
  return value.slice(0, 12);
};

/**
 * Limits medication form input to 255 characters
 * @param value - The input value
 * @returns Truncated value (max 255 characters)
 */
export const validateMedicationFormInput = (value: string): string => {
  return value.slice(0, 255);
};
